import React from 'react';
import { usePrimaryFill } from '../../hooks/usePrimaryFill';
import { useT } from '../../i18n';
import styles from './InlineContactSection.module.css';

interface InlineContactSectionProps {
  label: string;
  onClick: () => void;
  primaryColor?: string;
  primaryHoverColor?: string;
  useGradient?: boolean;
  emoji?: string;
  description?: string;
  /** When true, the button renders in its "active" state to signal the form
   * below is open. Clicking again should close the form. */
  isOpen?: boolean;
}

export function InlineContactSection({
  label,
  onClick,
  primaryColor,
  primaryHoverColor,
  useGradient = false,
  emoji,
  description,
  isOpen = false,
}: InlineContactSectionProps) {
  const t = useT();
  // The fill (and its hover swap) only applies in the closed/filled state.
  // When open the button is transparent with an inset border, so the var is
  // still set for that border + label color.
  const { style: fillStyle, onMouseEnter, onMouseLeave } = usePrimaryFill(
    isOpen ? undefined : primaryColor,
    primaryHoverColor,
    useGradient,
  );
  const style: React.CSSProperties = {
    ...(primaryColor
      ? ({ '--inline-contact-color': primaryColor } as React.CSSProperties)
      : {}),
    ...fillStyle,
  };

  const isImageEmoji =
    emoji && (emoji.startsWith('http') || emoji.startsWith('/'));

  return (
    <div className={styles.wrapper}>
      <p className={styles.description}>{description ?? t('contact.description')}</p>
      <button
        type="button"
        className={`${styles.button} ${isOpen ? styles.buttonOpen : ''}`}
        onClick={onClick}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-expanded={isOpen}
      >
        {emoji &&
          (isImageEmoji ? (
            <img src={emoji} alt="" className={styles.emojiImg} />
          ) : (
            <span className={styles.emoji}>{emoji}</span>
          ))}
        <span className={styles.label}>{label}</span>
      </button>
    </div>
  );
}
